'use client'

import { useCallback, useEffect, useState } from 'react'
import { TurnstileWidget } from './TurnstileWidget'
import { useTurnstileConfig } from './useTurnstileConfig'

/**
 * Solves the (usually invisible) Turnstile widget once per 30 minutes and
 * trades the token for the `pt_hv` cookie the ticket routes read
 * (lib/security/human-check). Mounted once in the storefront layout, so every
 * page that holds tickets is covered without each one rendering a widget.
 *
 * Renders nothing when the bot check is off for this host. Never blocks the
 * page: if the widget fails, buyers carry on under the server's fallback limit.
 */
const STORE_KEY = 'pt_hv_exp'
const REFRESH_MARGIN_MS = 2 * 60_000

function storedExpiry(): number {
  try { return Number(sessionStorage.getItem(STORE_KEY) || 0) } catch { return 0 }
}

export default function HumanCheck() {
  const config = useTurnstileConfig()
  const [round, setRound] = useState(0)
  const [needed, setNeeded] = useState(false)

  useEffect(() => {
    if (!config?.siteKey) return
    const exp = storedExpiry()
    const wait = exp - Date.now() - REFRESH_MARGIN_MS
    if (wait <= 0) { setNeeded(true); return }
    const t = setTimeout(() => { setNeeded(true); setRound((r) => r + 1) }, wait)
    return () => clearTimeout(t)
  }, [config?.siteKey, round])

  const onVerify = useCallback(async (token: string) => {
    if (!token) { setNeeded(false); return }
    try {
      const res = await fetch('/api/security/turnstile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json().catch(() => null)
      if (res.ok && data?.expiresIn) {
        try { sessionStorage.setItem(STORE_KEY, String(Date.now() + data.expiresIn * 1000)) } catch { /* private mode */ }
      }
    } catch { /* fallback limit applies server-side */ }
    setNeeded(false)
    setRound((r) => r + 1)
  }, [])

  if (!config?.siteKey || !needed) return null
  return (
    <div aria-hidden="true" style={{ position: 'fixed', bottom: 12, right: 12, zIndex: 60 }}>
      <TurnstileWidget key={round} onVerify={onVerify} appearance="interaction-only" />
    </div>
  )
}
